// COMPONENTS
import { BaseDetailRow } from "../../../../../components";

// TYPES
import { OrderContentSectionProps } from "../types";

// VIEW MODEL

const OrderDetailsContentSection = ({
	sectionTitle,
	data,
	fields,
	itemContainerClasses,
	iconStyle = "regular",
	mobileBreakpoint,
	sectionContainerClasses,
	detailsContainerClasses,
	children,
	textSize,
	defaultFlexDirection = "flex-row",
	approvalDecisions,
	currencyCharacter
}: OrderContentSectionProps) => {
	const findReasonForRequest = () => {
		const notes = data?.salesOrderNotes;
		return notes?.find(
			(note: { [key: string]: any }) =>
				note?.salesOrderNoteType?.name === "Request Reason",
		)?.note;
	};

	const findAssignedToForClientPreferredStage = (preferredStage: string) => {
		const stages = approvalDecisions?.stages;
		const foundPreferredStageDetails = stages?.find(
			(stage: any) =>
				stage?.ApprovalTemplateStages?.step === String(preferredStage),
		);
		if (
			foundPreferredStageDetails &&
			foundPreferredStageDetails.AssignedTo
		) {
			const assignedTo = foundPreferredStageDetails.AssignedTo;
			return `${assignedTo._name || "—"}${
				assignedTo.email ? ` (${assignedTo.email})` : ""
			}`;
		}
		return "—";
	};

	const findShippingInstructions = () => {
		const notes = data?.salesOrderNotes;
		return notes?.find(
			(note: { [key: string]: any }) =>
				note.salesOrderNoteType?.name === "Shipping Instructions",
		)?.note;
	};

	const getFieldValue = (valueKey: string) => {
		let value: any;
		if (valueKey === "shipToAddress") {
			value = [
				data?.shipToAddress?.addressee ?? "",
				data?.shipToAddress?.attention
					? `Attn: ${data?.shipToAddress.attention}`
					: "",
				data?.shipToAddress?.line1 ?? "",
				data?.shipToAddress?.line2 ?? "",
				`${data?.shipToAddress?.city ?? ""}, ${
					data?.shipToAddress?.state?.code ?? ""
				} ${data?.shipToAddress?.zip ?? ""}`,
				data?.shipToAddress?.phoneNumber ?? "",
			];
		} else if (valueKey === "reasonForRequest") {
			value = findReasonForRequest() || "—";
		} else if (valueKey === "shippingInstructions") {
			value = findShippingInstructions() || "—";
		} else if (valueKey === "salesOrderEmailAddresses") {
			value = data?.salesOrderEmailAddresses;
		} else if (valueKey === "orderFor") {
			const orderFor = `${data?.contact?._name}  ${
				data?.contact?.primaryContactEmailAddress?.emailAddress
					? ` (${data?.contact?.primaryContactEmailAddress?.emailAddress})`
					: ""
			}`;
			value = orderFor;
		} else {
			// function that allows us to access nested object props regardless of how nested they are
			value = valueKey
				.split(".")
				.reduce((accum, currentValue) => accum?.[currentValue], data);

			if (value === undefined || value === null || value === "") {
				value = "—";
			}
		}
		return value;
	};

	let sectionContent;

	if (children) {
		sectionContent = children;
	} else {
		sectionContent = fields?.map(({ uuid, valueKey, ...field }) => {
			if (field.isVisible === false) {
				return null;
			}
			let value = getFieldValue(valueKey);

			// FORMATTING DATA TO SHOW CONTRACTOR INFO IN THE 'ORDER FOR' SECTION, IF CONDITIONS ARE MET

			if (valueKey === "assignedTo") {
				value =
					findAssignedToForClientPreferredStage(
						field.preferredStage as string,
					) || value;
			}

			if (valueKey === "salesOrderEmailAddresses") {
				if (value?.length > 0) {
					const checkEmail = value.map(
						(email: { [key: string]: any }) => email?.emailAddress,
					);
					value = checkEmail;
				} else {
					value = "—";
				}
			}

			if (
				valueKey === "contact._name" &&
				data?.salesOrderNotes &&
				data?.salesOrderNotes.length > 0
			) {
				let contractorInfo = data.salesOrderNotes.find(
					(note: { [key: string]: any }) => {
						return (
							note.salesOrderNoteType?.name ===
							"Contractor Duration"
						);
					},
				);
				if (
					contractorInfo !== undefined &&
					contractorInfo !== null &&
					field.isDoubleValue
				) {
					value = `${value} (Contractor, ${contractorInfo.note})${
						data?.contact?.primaryContactEmailAddress?.emailAddress
							? ` (${data.contact.primaryContactEmailAddress.emailAddress})`
							: ""
					}`;
				} else if (field.isDoubleValue) {
					value = `${value}${
						data?.contact?.primaryContactEmailAddress?.emailAddress
							? ` (${data.contact.primaryContactEmailAddress.emailAddress})`
							: ""
					}`;
				} else {
					value = value;
				}
			}

			return (
				<BaseDetailRow
					key={valueKey}
					dataItem={value}
					allData={data}
					field={{
						valueKey,
						...field,
					}}
					allFields={fields}
					containerWidth={itemContainerClasses}
					mobileBreakpoint={mobileBreakpoint}
					iconStyle={iconStyle}
					textSize={textSize}
					defaultFlexDirection={defaultFlexDirection}
					currencyCharacter={currencyCharacter}
				/>
			);
		});
	}

	return (
		<section className={`${sectionContainerClasses}`}>
			{sectionTitle}
			<div className={`${detailsContainerClasses}`}>{sectionContent}</div>
		</section>
	);
};

export default OrderDetailsContentSection;
